"use client";
// ===================================================
// GAM Command Center — chat unread state, sound and browser notification.
//
// This is the piece that answers Gal's real worry about the WhatsApp move: WhatsApp
// rings a phone, a web page is silent. So the badge/sound/notification subscribe to
// EVERY visible conversation, not just the open one.
//
// "Seen" is tracked per conversation in localStorage as an ISO timestamp. A message
// newer than that mark counts as unread. Deliberately local, not a DB column — read
// receipts are a product feature with its own schema, and stage 1 does not need one.
//
// Spec: docs/specs/chat-substrate-stage1-internal-alerts.md §5.3
// ===================================================
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchConversations,
  fetchMessages,
  subscribeToAllMessages,
  unsubscribe,
  type ChatConversation,
  type ChatMessage,
} from "@/lib/supabase/chatQueries";
import { playChatSound, DEFAULT_SOUND_ID } from "@/lib/chat/notificationSounds";
import { supabase } from "@/lib/supabaseClient";

const SEEN_KEY = "gam_chat_last_seen";
const SOUND_KEY = "gam_chat_sound";
const NOTIFY_KEY = "gam_chat_browser_notify";

type SeenMap = Record<string, string>;

function readSeen(): SeenMap {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(SEEN_KEY) ?? "{}") as SeenMap;
  } catch {
    return {};
  }
}

function writeSeen(map: SeenMap): void {
  try {
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    /* private mode — unread simply won't persist across reloads */
  }
}

interface ChatNotificationsValue {
  conversations: ChatConversation[];
  /** unread count per conversation id */
  unread: Record<string, number>;
  totalUnread: number;
  markSeen: (conversationId: string) => void;
  soundId: string;
  setSoundId: (id: string) => void;
  browserNotify: boolean;
  setBrowserNotify: (on: boolean) => Promise<void>;
  /** the most recent message seen on the wire — lets an open surface append without refetching */
  lastMessage: ChatMessage | null;
}

const ChatNotificationsContext = createContext<ChatNotificationsValue>({
  conversations: [],
  unread: {},
  totalUnread: 0,
  markSeen: () => {},
  soundId: DEFAULT_SOUND_ID,
  setSoundId: () => {},
  browserNotify: false,
  setBrowserNotify: async () => {},
  lastMessage: null,
});

export function ChatNotificationsProvider({ children }: { children: React.ReactNode }) {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [unread, setUnread] = useState<Record<string, number>>({});
  const [lastMessage, setLastMessage] = useState<ChatMessage | null>(null);
  const [soundId, setSoundIdState] = useState(DEFAULT_SOUND_ID);
  const [browserNotify, setBrowserNotifyState] = useState(false);

  const seenRef = useRef<SeenMap>({});
  const convRef = useRef<ChatConversation[]>([]);
  const soundRef = useRef(soundId);
  const notifyRef = useRef(browserNotify);
  const selfIdRef = useRef<string | null>(null);

  soundRef.current = soundId;
  notifyRef.current = browserNotify;
  convRef.current = conversations;

  // ── preferences ──
  useEffect(() => {
    seenRef.current = readSeen();
    try {
      setSoundIdState(window.localStorage.getItem(SOUND_KEY) ?? DEFAULT_SOUND_ID);
      setBrowserNotifyState(window.localStorage.getItem(NOTIFY_KEY) === "1");
    } catch {
      /* ignore */
    }
  }, []);

  const setSoundId = useCallback((id: string) => {
    setSoundIdState(id);
    try {
      window.localStorage.setItem(SOUND_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const setBrowserNotify = useCallback(async (on: boolean) => {
    if (on && typeof Notification !== "undefined" && Notification.permission !== "granted") {
      const res = await Notification.requestPermission();
      if (res !== "granted") {
        setBrowserNotifyState(false);
        return;
      }
    }
    setBrowserNotifyState(on);
    try {
      window.localStorage.setItem(NOTIFY_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  // ── initial load: conversations + backlog unread ──
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return; // signed out — nothing is visible anyway
      selfIdRef.current = auth.user.id;

      let convs: ChatConversation[];
      try {
        convs = await fetchConversations();
      } catch {
        return; // RLS or offline — the badge simply stays empty
      }
      if (cancelled) return;
      setConversations(convs);

      const counts: Record<string, number> = {};
      await Promise.all(
        convs.map(async (c) => {
          try {
            const msgs = await fetchMessages(c.id, 100);
            const seenAt = seenRef.current[c.id];
            counts[c.id] = msgs.filter(
              (m) => m.sender_id !== selfIdRef.current && (!seenAt || m.created_at > seenAt)
            ).length;
          } catch {
            counts[c.id] = 0;
          }
        })
      );
      if (!cancelled) setUnread(counts);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ── live: one subscription for every visible conversation ──
  useEffect(() => {
    const channel = subscribeToAllMessages((msg) => {
      setLastMessage(msg);
      if (msg.sender_id === selfIdRef.current) return; // your own message is not a notification

      setUnread((prev) => ({ ...prev, [msg.conversation_id]: (prev[msg.conversation_id] ?? 0) + 1 }));
      playChatSound(soundRef.current);

      if (notifyRef.current && typeof Notification !== "undefined" && Notification.permission === "granted") {
        const title = convRef.current.find((c) => c.id === msg.conversation_id)?.title ?? "צ׳אט";
        try {
          new Notification(title, { body: msg.body.slice(0, 180), tag: msg.conversation_id });
        } catch {
          /* some browsers require a service worker — degrade to badge + sound */
        }
      }
    });
    return () => unsubscribe(channel);
  }, []);

  const markSeen = useCallback((conversationId: string) => {
    seenRef.current = { ...seenRef.current, [conversationId]: new Date().toISOString() };
    writeSeen(seenRef.current);
    setUnread((prev) => (prev[conversationId] ? { ...prev, [conversationId]: 0 } : prev));
  }, []);

  const totalUnread = useMemo(() => Object.values(unread).reduce((a, b) => a + b, 0), [unread]);

  const value = useMemo<ChatNotificationsValue>(
    () => ({
      conversations,
      unread,
      totalUnread,
      markSeen,
      soundId,
      setSoundId,
      browserNotify,
      setBrowserNotify,
      lastMessage,
    }),
    [conversations, unread, totalUnread, markSeen, soundId, setSoundId, browserNotify, setBrowserNotify, lastMessage]
  );

  return <ChatNotificationsContext.Provider value={value}>{children}</ChatNotificationsContext.Provider>;
}

export function useChatNotifications() {
  return useContext(ChatNotificationsContext);
}
